import { Auth0Options, Auth0Service } from '@Modules/auth_module';
import { Logger, LoggerService } from '@Shared/logger';
import { UIUtil } from '@Shared/utils';
import { createTemplate, Template } from '../template';
import { UI_CONST } from '../display';
import { IScreen, ScreenType } from './models';
import * as RegisteredScreens from './screens/index';

export class ScreenManager {
  private Logger: Logger = LoggerService.createLogger('ScreenManager');

  private static instance: ScreenManager;

  static get Instance() {
    if (this.instance === null || this.instance === undefined) {
      this.instance = new ScreenManager();
    }
    return this.instance;
  }

  private template: Template | null = null;

  get Template(): Template | null {
    return this.template;
  }

  private currentScreen: IScreen;

  get CurrentScreen(): IScreen | null {
    return this.currentScreen;
  }

  constructor() {
    this.Logger.trace('****  CTOR ****');
    this.registerEvents();
  }

  public showScreen(screen: ScreenType): void {
    this.Logger.trace('showScreen', screen);
    // `template` is only built by setup(), which runs in AuthModule.start() —
    // after the site config and html.json round trips. But showScreen is already
    // reachable via window.MNGAuth and via the delegated click handler wired in
    // the ctor, so it can be called during that window. Warn instead of throwing.
    if (!this.template) {
      this.Logger.warn(
        'showScreen called before the plugin finished initializing; ignoring',
        screen,
      );
      return;
    }
    this.template.setup();
    if (this.currentScreen) {
      this.Logger.trace('has currentScreen...close first');
      this.currentScreen.close();
    }
    this.currentScreen = new RegisteredScreens[screen.toString()]() as IScreen;
    this.template.showTemplate();
    this.currentScreen.show();
  }

  public closeScreen(): void {
    this.Logger.trace('closeScreen');

    if (this.currentScreen) {
      this.Logger.trace('has currentScreen...close first');
      this.currentScreen.close();
      this.template?.closeTemplate();
    }
  }

  public setup(opts: Auth0Options): void {
    const modeOptions = opts.mode;
    if (modeOptions.type === 'embedded') {
      this.Logger.debug('Setting up screen manager for embedded mode');
      this.template = createTemplate(modeOptions);

      //if we are in inline mode, check if that selector exists, if it doesn't then fallback to modal.
    } else {
      this.Logger.warn('We currently only support Embedded Mode.');
    }
  }

  private registerEvents(): void {
    this.Logger.trace('registerEvents');

    UIUtil.registerDynamicClickEvent<HTMLAnchorElement>(
      UI_CONST.authActionShowScreen,
      (event, element) => {
        const authAction = element.dataset.mngAuthShowScreen;
        this.Logger.trace(
          'Show Screen Clicked:',
          'element',
          element,
          'authAction',
          authAction,
        );

        this.showScreen(authAction as ScreenType);
      },
    );

    UIUtil.registerDynamicClickEvent<HTMLAnchorElement>(
      UI_CONST.authSocialAction,
      (event, element) => {
        const authAction = element.dataset.mngAuthSocialAction;
        this.Logger.trace(
          'Show Screen Clicked:',
          'element',
          element,
          'authAction',
          authAction,
        );
        Auth0Service.Instance.loginWithSocial(authAction);
      },
    );
  }
}
